import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Receipt, Download } from 'lucide-react'
import { paymentAPI } from '../services/api'
import type { Payment } from '../types'
import InvoiceGenerator from '../components/InvoiceGenerator'

const Payments: React.FC = () => {
  const { t } = useTranslation()
  const [payments, setPayments] = useState<Payment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false)

  useEffect(() => {
    fetchPayments()
  }, [])

  const fetchPayments = async () => {
    try {
      const response = await paymentAPI.getPaymentHistory()
      // const user = JSON.parse(localStorage.getItem('user') || '{}')
      
      // Użyj danych bezpośrednio z API
      setPayments(response.data)
    } catch (error) {
      console.error('Error fetching payments:', error)
      setPayments([])
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'bg-green-900/20 text-green-400 border-green-900/50'
      case 'pending':
        return 'bg-yellow-900/20 text-yellow-400 border-yellow-900/50'
      case 'failed':
      case 'cancelled':
        return 'bg-red-900/20 text-red-400 border-red-900/50'
      default:
        return 'bg-gray-900/20 text-gray-400 border-gray-900/50'
    }
  }

  const handleGenerateInvoice = (payment: Payment) => {
    setSelectedPayment(payment)
    setShowInvoiceGenerator(true)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('payments.title')}</h1>
        <p className="text-gray-400">{t('payments.subtitle')}</p>
      </div>

      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Receipt className="h-5 w-5 text-primary mr-2" />
          {t('payments.paymentHistory')}
        </h2>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse bg-dark-700 h-20 rounded"></div>
            ))}
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">{t('payments.noPayments')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('payments.date')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('payments.amount')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('payments.method')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('payments.status')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('payments.invoice')}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className="border-b border-dark-700 hover:bg-dark-700/50">
                    <td className="py-4 px-4">
                      <p className="text-gray-300">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="font-medium text-white">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: payment.currency
                        }).format(payment.amount)}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-300 capitalize">{payment.payment_method}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      {payment.status === 'completed' && (
                        <button 
                          onClick={() => handleGenerateInvoice(payment)}
                          className="flex items-center space-x-1 text-primary hover:text-primary/80"
                        >
                          <Download className="h-4 w-4" />
                          <span>{t('payments.download')}</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice Generator */}
      {selectedPayment && (
        <InvoiceGenerator
          payment={selectedPayment as any}
          isOpen={showInvoiceGenerator}
          onClose={() => {
            setShowInvoiceGenerator(false)
            setSelectedPayment(null)
          }}
        />
      )}
    </div>
  )
}

export default Payments